import React, { useCallback, useEffect, useRef, useState } from "react";
import { inject, observer } from "mobx-react";
import { RotateCcw } from "lucide-react";
import {
  registerCancelTransition,
  registerRevertForOverride,
  setDeckRate,
} from "../Utils/djAudioController";
import { showToast } from "../Utils/toast";
import { subscribeTicker } from "../Utils/djTicker";
import {
  STRATEGY,
  chooseStrategy,
  pickNextTrack,
  computeAutoArc,
} from "../Utils/djAlgos";
import Deck from "./Dj/Deck";
import MasterControls from "./Dj/MasterControls";
import NextUpPanel from "./Dj/NextUpPanel";
import {
  BEATS_PER_BAR,
  findNextBeat,
  findNextDownbeat,
  lastBeatAtOrBefore,
  nearestBeat,
  resolveStrategy,
} from "./Dj/utils";

const STRATEGY_BARS = {
  [STRATEGY.MATCHED_16]: 16,
  [STRATEGY.SHORT_4]: 4,
};
const POST_FADE_DRIFT_BARS = 8;
const RATE_DRIFT_EPSILON = 0.001;
const PLL_GAIN = 0.001;
const PLL_MAX_DEV = 0.005;

function Dj({ DjStore }) {
  const isPortraitPhone = () =>
    window.innerWidth < 900 && window.matchMedia("(orientation: portrait)").matches;
  const [needsRotate, setNeedsRotate] = useState(isPortraitPhone);
  useEffect(() => {
    const onResize = () => setNeedsRotate(isPortraitPhone());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const audioCtxRef = useRef(null);
  const masterGainRef = useRef(null);
  const audioARef = useRef(null);
  const audioBRef = useRef(null);
  const gainARef = useRef(null);
  const gainBRef = useRef(null);
  const sourceARef = useRef(null);
  const sourceBRef = useRef(null);
  const transitionRef = useRef(null);
  const tickRef = useRef(null);

  const ensureAudioContext = useCallback(() => {
    if (audioCtxRef.current) {
      if (audioCtxRef.current.state === "suspended") audioCtxRef.current.resume();
      return audioCtxRef.current;
    }
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const master = ctx.createGain();
    master.gain.value = 1;
    master.connect(ctx.destination);
    audioCtxRef.current = ctx;
    masterGainRef.current = master;
    return ctx;
  }, []);

  const applyCrossfade = useCallback((fade) => {
    const gA = Math.cos((fade * Math.PI) / 2);
    const gB = Math.sin((fade * Math.PI) / 2);
    if (gainARef.current) gainARef.current.gain.value = gA;
    if (gainBRef.current) gainBRef.current.gain.value = gB;
  }, []);

  const pickFromPool = useCallback((currentAnalysis, pool, fullQueue) => {
    if (!pool?.length) return null;
    const current = currentAnalysis
      ? {
          bpm: currentAnalysis.bpm ?? null,
          camelot: currentAnalysis.camelot ?? null,
          energy: currentAnalysis.energy ?? null,
        }
      : null;
    const total = fullQueue?.length || pool.length + DjStore.playedIds.length;
    const queueProgress = total > 0 ? DjStore.playedIds.length / total : 0;
    const energyTarget = computeAutoArc({
      hourOfDay: new Date().getHours(),
      queueProgress,
    });
    return pickNextTrack(current, pool, DjStore.getFeatureCacheMap(), {
      energyTarget,
    });
  }, [DjStore]);

  useEffect(() => {
    applyCrossfade(DjStore.crossfade);
  }, [DjStore.crossfade, applyCrossfade]);

  const connectDeck = useCallback((side) => {
    const ctx = ensureAudioContext();
    const audioEl = side === "A" ? audioARef.current : audioBRef.current;
    if (!audioEl) return;
    const existingSource = side === "A" ? sourceARef.current : sourceBRef.current;
    if (existingSource) return;
    const gain = ctx.createGain();
    const source = ctx.createMediaElementSource(audioEl);
    source.connect(gain).connect(masterGainRef.current);
    if (side === "A") {
      gainARef.current = gain;
      sourceARef.current = source;
    } else {
      gainBRef.current = gain;
      sourceBRef.current = source;
    }
    applyCrossfade(DjStore.crossfade);
  }, [ensureAudioContext, applyCrossfade, DjStore.crossfade]);

  const handlePlayPause = useCallback((side) => {
    ensureAudioContext();
    const audioEl = side === "A" ? audioARef.current : audioBRef.current;
    if (!audioEl) return;
    if (audioEl.paused) {
      audioEl.play().catch((err) => {
        const deck = side === "A" ? DjStore.deckA : DjStore.deckB;
        showToast.error(`Deck ${side} — ${err?.message || "could not start playback"}: "${deck.track?.title || "track"}"`);
      });
    } else {
      audioEl.pause();
    }
  }, [ensureAudioContext, DjStore]);

  const handleCue = useCallback((side) => {
    const deck = side === "A" ? DjStore.deckA : DjStore.deckB;
    const audioEl = side === "A" ? audioARef.current : audioBRef.current;
    if (!audioEl || !deck.analysis) return;
    audioEl.currentTime = (deck.analysis.introEndMs ?? 0) / 1000;
  }, [DjStore.deckA, DjStore.deckB]);

  const handleSync = useCallback((targetSide) => {
    const active = targetSide === "A" ? DjStore.deckB : DjStore.deckA;
    const target = targetSide === "A" ? DjStore.deckA : DjStore.deckB;
    const activeEl = targetSide === "A" ? audioBRef.current : audioARef.current;
    const targetEl = targetSide === "A" ? audioARef.current : audioBRef.current;
    if (!active.analysis || !target.analysis || !activeEl || !targetEl) return;

    const activeNowMs = activeEl.currentTime * 1000;
    const targetNowMs = targetEl.currentTime * 1000;
    const activeNextBeat = findNextBeat(active.analysis.beatGrid, activeNowMs);
    if (activeNextBeat == null) return;
    const delta = activeNextBeat - activeNowMs;
    const targetAligned = nearestBeat(target.analysis.beatGrid, targetNowMs + delta);
    if (targetAligned == null) return;
    targetEl.currentTime = Math.max(0, (targetAligned - delta) / 1000);
  }, [DjStore.deckA, DjStore.deckB]);

  const cancelTransition = useCallback(() => {
    if (transitionRef.current?.unsubscribe) transitionRef.current.unsubscribe();
    DjStore.restoreUserRate("A");
    DjStore.restoreUserRate("B");
    transitionRef.current = null;
    DjStore.setTransitionFading(false);
  }, [DjStore]);

  const revertTransition = useCallback(() => {
    const t = transitionRef.current;
    if (!t) return;
    const incomingSide = t.activeSide === "A" ? "B" : "A";
    const incomingAudio = incomingSide === "A" ? audioARef.current : audioBRef.current;
    if (incomingAudio && !incomingAudio.paused) incomingAudio.pause();
    const restoreCrossfade = t.fadeFrom != null ? t.fadeFrom : (t.activeSide === "A" ? 0 : 1);
    DjStore.setCrossfade(restoreCrossfade);
    cancelTransition();
    DjStore.setAutoTransition(false);
    DjStore.setQueueMode(false);
    DjStore.setNextTrack(null);
  }, [DjStore, cancelTransition]);

  const accelerateTransition = useCallback(() => {
    const t = transitionRef.current;
    if (!t) return;
    // Halve remaining time on each press — N presses ⇒ 2^N speedup of the rest of the transition.
    const factor = 0.5;
    const now = performance.now();
    if (t.state === "fading" && t.fadeStart != null && t.fadeDurationMs != null) {
      const elapsed = now - t.fadeStart;
      const remaining = Math.max(0, t.fadeDurationMs - elapsed);
      t.fadeDurationMs = elapsed + remaining * factor;
    }
    if (t.state === "drifting" && t.driftStart != null && t.driftDurationMs != null) {
      const elapsed = now - t.driftStart;
      const remaining = Math.max(0, t.driftDurationMs - elapsed);
      t.driftDurationMs = elapsed + remaining * factor;
    }
  }, []);

  const startFade = useCallback((t) => {
    const { activeSide } = t;
    const incomingSide = activeSide === "A" ? "B" : "A";
    const activeDeck = activeSide === "A" ? DjStore.deckA : DjStore.deckB;
    const incomingDeck = incomingSide === "A" ? DjStore.deckA : DjStore.deckB;
    const activeAudio = activeSide === "A" ? audioARef.current : audioBRef.current;
    const incomingAudio = incomingSide === "A" ? audioARef.current : audioBRef.current;

    const outBpm = activeDeck.analysis?.bpm;
    const inBpm = incomingDeck.analysis?.bpm;
    t.strategy = resolveStrategy(DjStore, outBpm, inBpm);
    const strategy = t.strategy;

    t.fadeFrom = DjStore.crossfade;

    if (strategy === STRATEGY.CUT) {
      t.state = "cutting";
      DjStore.setTransitionFading(true);
      return;
    }

    const bars = STRATEGY_BARS[strategy] ?? 4;
    t.fadeDurationMs = (60000 / activeDeck.analysis.bpm) * BEATS_PER_BAR * bars;
    t.fadeTo = activeSide === "A" ? 1 : 0;
    t.fadeStart = performance.now();
    t.state = "fading";
    DjStore.setTransitionFading(true);

    if (incomingAudio && incomingDeck.analysis) {
      let startMs = incomingDeck.analysis.introEndMs ?? 0;
      if (strategy === STRATEGY.MATCHED_16) {
        t.matchedRate = activeDeck.analysis.bpm / incomingDeck.analysis.bpm;
        DjStore.setEffectiveRate(incomingSide, t.matchedRate);
        const outBeats = activeDeck.analysis.beatGrid;
        if (activeAudio && outBeats && outBeats.length) {
          const outNowMs = activeAudio.currentTime * 1000;
          const lastBeat = lastBeatAtOrBefore(outBeats, outNowMs);
          if (lastBeat != null) {
            startMs += (outNowMs - lastBeat) * t.matchedRate;
          }
        }
      } else {
        DjStore.restoreUserRate(incomingSide);
      }
      incomingAudio.currentTime = startMs / 1000;
      incomingAudio.play().catch(() => {});
    }
  }, [DjStore]);

  const finishTransition = useCallback((t) => {
    const { activeSide } = t;
    const incomingSide = activeSide === "A" ? "B" : "A";
    const oldActiveTrack = activeSide === "A" ? DjStore.deckA.track : DjStore.deckB.track;

    DjStore.setTransitionFading(false);

    if (t.queueMode) {
      if (oldActiveTrack) DjStore.addPlayed(oldActiveTrack.mediaId);
      t.activeSide = incomingSide;

      const newActiveDeck = incomingSide === "A" ? DjStore.deckA : DjStore.deckB;
      const newActiveAnalysis = newActiveDeck.analysis;

      const pool = DjStore.queue.filter((track) => {
        if (track.mediaId === newActiveDeck.track?.mediaId) return false;
        if (DjStore.playedIds.includes(track.mediaId)) return false;
        return true;
      });
      const picked = pickFromPool(newActiveAnalysis, pool, DjStore.queue);

      if (!picked) {
        cancelTransition();
        DjStore.setQueueMode(false);
        DjStore.setNextTrack(null);
        return;
      }

      DjStore.setNextTrack(picked.track);
      DjStore.setLastPickBreakdown(picked.breakdown);
      DjStore.loadToDeck(picked.track, activeSide);

      const nextBpm = picked.candidateFeatures?.bpm;
      t.strategy = resolveStrategy(DjStore, newActiveAnalysis?.bpm, nextBpm);
      t.state = "waiting-outro";
    } else {
      cancelTransition();
      DjStore.setAutoTransition(false);
      DjStore.setNextTrack(null);
    }
  }, [DjStore, cancelTransition, pickFromPool]);

  const transitionTick = useCallback(() => {
    const t = transitionRef.current;
    if (!t) return;
    const { activeSide, strategy } = t;
    const incomingSide = activeSide === "A" ? "B" : "A";
    const activeDeck = activeSide === "A" ? DjStore.deckA : DjStore.deckB;
    const incomingDeck = incomingSide === "A" ? DjStore.deckA : DjStore.deckB;
    const activeAudio = activeSide === "A" ? audioARef.current : audioBRef.current;
    const incomingAudio = incomingSide === "A" ? audioARef.current : audioBRef.current;

    if (!activeAudio) {
      return;
    }

    if (t.state === "waiting-outro") {
      const nowMs = activeAudio.currentTime * 1000;
      const outroStart = activeDeck.analysis?.outroStartMs;
      const ready =
        outroStart != null &&
        nowMs >= outroStart &&
        incomingDeck.analysis &&
        incomingAudio;
      if (ready) startFade(t);
      return;
    }

    if (t.state === "waiting-now") {
      if (incomingDeck.analysis && incomingAudio) startFade(t);
      return;
    }

    if (t.state === "cutting") {
      const nowMs = activeAudio.currentTime * 1000;
      const nextDownbeat = findNextDownbeat(activeDeck.analysis?.beatGrid, nowMs);
      const shouldCut = nextDownbeat != null && nowMs >= nextDownbeat - 30;
      if (shouldCut && incomingAudio && incomingDeck.analysis) {
        incomingAudio.currentTime = (incomingDeck.analysis.introEndMs ?? 0) / 1000;
        incomingAudio.play().catch(() => {});
        const cutTo = activeSide === "A" ? 1 : 0;
        DjStore.setCrossfade(cutTo);
        applyCrossfade(cutTo);
        activeAudio.pause();
        finishTransition(t);
        return;
      }
      return;
    }

    if (t.state === "fading") {
      const elapsed = performance.now() - t.fadeStart;
      const pct = Math.min(1, elapsed / t.fadeDurationMs);
      const newCross = t.fadeFrom + (t.fadeTo - t.fadeFrom) * pct;
      DjStore.setCrossfade(newCross);
      applyCrossfade(newCross);

      if (
        strategy === STRATEGY.MATCHED_16 &&
        t.matchedRate != null &&
        pct < 1 &&
        incomingAudio
      ) {
        const outBeats = activeDeck.analysis?.beatGrid;
        const inBeats = incomingDeck.analysis?.beatGrid;
        if (outBeats?.length && inBeats?.length) {
          const outNowMs = activeAudio.currentTime * 1000;
          const inNowMs = incomingAudio.currentTime * 1000;
          const outNext = findNextBeat(outBeats, outNowMs);
          const inNext = findNextBeat(inBeats, inNowMs);
          if (outNext != null && inNext != null) {
            const beatDuration = 60000 / activeDeck.analysis.bpm;
            const currentRate = incomingAudio.playbackRate || t.matchedRate;
            const outDt = outNext - outNowMs;
            const inDt = (inNext - inNowMs) / currentRate;
            let errMs = inDt - outDt;
            const halfBeat = beatDuration / 2;
            while (errMs > halfBeat) errMs -= beatDuration;
            while (errMs < -halfBeat) errMs += beatDuration;
            let correction = errMs * PLL_GAIN;
            if (correction > PLL_MAX_DEV) correction = PLL_MAX_DEV;
            if (correction < -PLL_MAX_DEV) correction = -PLL_MAX_DEV;
            setDeckRate(incomingSide, t.matchedRate * (1 + correction));
          }
        }
      }

      if (pct >= 1) {
        if (activeAudio) activeAudio.pause();

        if (strategy === STRATEGY.MATCHED_16 && t.matchedRate != null) {
          const driftTo = incomingDeck.userRate ?? 1;
          if (Math.abs(driftTo - t.matchedRate) < RATE_DRIFT_EPSILON) {
            DjStore.restoreUserRate(incomingSide);
            finishTransition(t);
            return;
          }
          t.driftFrom = t.matchedRate;
          t.driftTo = driftTo;
          t.driftDurationMs =
            (60000 / activeDeck.analysis.bpm) * BEATS_PER_BAR * POST_FADE_DRIFT_BARS;
          t.driftStart = performance.now();
          t.state = "drifting";
          return;
        }

        finishTransition(t);
        return;
      }
    }

    if (t.state === "drifting") {
      const elapsed = performance.now() - t.driftStart;
      const pct = Math.min(1, elapsed / t.driftDurationMs);
      const rate = t.driftFrom + (t.driftTo - t.driftFrom) * pct;
      DjStore.setEffectiveRate(incomingSide, rate);
      if (pct >= 1) {
        DjStore.restoreUserRate(incomingSide);
        finishTransition(t);
      }
      return;
    }
  }, [DjStore, startFade, finishTransition, applyCrossfade]);

  useEffect(() => {
    tickRef.current = transitionTick;
  }, [transitionTick]);

  const softRevertForOverride = useCallback(() => {
    const t = transitionRef.current;
    if (!t) return;
    const activeSide = t.activeSide;
    const incomingSide = activeSide === "A" ? "B" : "A";
    const incomingAudio = incomingSide === "A" ? audioARef.current : audioBRef.current;

    if (incomingAudio && !incomingAudio.paused) incomingAudio.pause();
    const restoreCrossfade = t.fadeFrom != null ? t.fadeFrom : (activeSide === "A" ? 0 : 1);
    DjStore.setCrossfade(restoreCrossfade);
    applyCrossfade(restoreCrossfade);

    cancelTransition();

    if (DjStore.queueMode) {
      transitionRef.current = {
        queueMode: true,
        activeSide,
        state: "waiting-outro",
        strategy: null,
        unsubscribe: null,
      };
      transitionRef.current.unsubscribe = subscribeTicker(
        () => tickRef.current && tickRef.current()
      );
    }
  }, [DjStore, cancelTransition, applyCrossfade]);

  const handleAutoTransition = useCallback(async () => {
    const t = transitionRef.current;
    if (t?.queueMode) {
      if (t.state === "waiting-outro") t.state = "waiting-now";
      return;
    }
    if (t) {
      cancelTransition();
      DjStore.setAutoTransition(false);
      DjStore.setNextTrack(null);
      return;
    }

    ensureAudioContext();

    let activeSide;
    if (DjStore.deckA.isPlaying) activeSide = "A";
    else if (DjStore.deckB.isPlaying) activeSide = "B";
    else if (DjStore.deckA.track) activeSide = "A";
    else if (DjStore.deckB.track) activeSide = "B";
    else return;

    const incomingSide = activeSide === "A" ? "B" : "A";
    let incomingDeck = incomingSide === "A" ? DjStore.deckA : DjStore.deckB;
    const activeDeck = activeSide === "A" ? DjStore.deckA : DjStore.deckB;

    if (!incomingDeck.track) {
      if (!DjStore.queue.length) return;
      const pool = DjStore.queue.filter(
        (track) => track.mediaId !== activeDeck.track?.mediaId
      );
      const picked = pickFromPool(activeDeck.analysis, pool, DjStore.queue);
      if (!picked) return;
      DjStore.setLastPickBreakdown(picked.breakdown);
      await DjStore.loadToDeck(picked.track, incomingSide);
      incomingDeck = incomingSide === "A" ? DjStore.deckA : DjStore.deckB;
    }

    const activeAudioEl = activeSide === "A" ? audioARef.current : audioBRef.current;
    if (activeAudioEl && activeAudioEl.paused) {
      activeAudioEl.play().catch(() => {});
    }

    DjStore.setNextTrack(incomingDeck.track);
    DjStore.setAutoTransition(true);

    const strategy = resolveStrategy(
      DjStore,
      activeDeck.analysis?.bpm,
      incomingDeck.analysis?.bpm
    );

    transitionRef.current = {
      queueMode: false,
      activeSide,
      state: "waiting-now",
      strategy,
      unsubscribe: null,
    };
    transitionRef.current.unsubscribe = subscribeTicker(() => tickRef.current && tickRef.current());
  }, [DjStore, ensureAudioContext, cancelTransition, pickFromPool]);

  const startQueueMode = useCallback(async () => {
    if (DjStore.queueMode) {
      DjStore.setQueueMode(false);
      cancelTransition();
      DjStore.setNextTrack(null);
      return;
    }
    if (!DjStore.queue.length) return;

    ensureAudioContext();
    DjStore.clearPlayed();
    DjStore.prewarmQueue();

    let activeSide;
    const deckAHasTrack = !!DjStore.deckA.track;
    const deckBHasTrack = !!DjStore.deckB.track;

    if (deckAHasTrack || deckBHasTrack) {
      if (DjStore.deckA.isPlaying) activeSide = "A";
      else if (DjStore.deckB.isPlaying) activeSide = "B";
      else if (deckAHasTrack) activeSide = "A";
      else activeSide = "B";

      DjStore.setCrossfade(activeSide === "A" ? 0 : 1);
      const activeAudioEl = activeSide === "A" ? audioARef.current : audioBRef.current;
      if (activeAudioEl && activeAudioEl.paused) activeAudioEl.play().catch(() => {});
    } else {
      activeSide = "A";
      await DjStore.loadToDeck(DjStore.queue[0], "A");
      DjStore.setCrossfade(0);
      setTimeout(() => {
        const audio = audioARef.current;
        if (!audio) return;
        audio.currentTime = 0;
        audio.play().catch(() => {});
      }, 300);
    }

    const activeDeck = activeSide === "A" ? DjStore.deckA : DjStore.deckB;
    const activeAnalysis = activeDeck.analysis;
    const pool = DjStore.queue.filter(
      (track) => track.mediaId !== activeDeck.track?.mediaId
    );
    const picked = pickFromPool(activeAnalysis, pool, DjStore.queue);

    if (!picked) {
      DjStore.setQueueMode(true);
      return;
    }

    const incomingSide = activeSide === "A" ? "B" : "A";
    const incomingDeck = incomingSide === "A" ? DjStore.deckA : DjStore.deckB;
    if (incomingDeck.track?.mediaId !== picked.track.mediaId) {
      DjStore.loadToDeck(picked.track, incomingSide);
    }
    DjStore.setNextTrack(picked.track);
    DjStore.setLastPickBreakdown(picked.breakdown);
    DjStore.setQueueMode(true);

    const nextBpm = picked.candidateFeatures?.bpm;
    const strategy = resolveStrategy(DjStore, activeAnalysis?.bpm, nextBpm);

    transitionRef.current = {
      queueMode: true,
      activeSide,
      state: "waiting-outro",
      strategy,
      unsubscribe: null,
    };
    transitionRef.current.unsubscribe = subscribeTicker(() => tickRef.current && tickRef.current());
  }, [DjStore, ensureAudioContext, cancelTransition, pickFromPool]);

  useEffect(() => {
    registerCancelTransition(() => {
      cancelTransition();
      DjStore.setAutoTransition(false);
      DjStore.setQueueMode(false);
      DjStore.setNextTrack(null);
    });
    registerRevertForOverride(softRevertForOverride);
    return () => {
      cancelTransition();
      registerCancelTransition(null);
      registerRevertForOverride(null);
    };
  }, [cancelTransition, softRevertForOverride, DjStore]);

  if (needsRotate) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-neutral-900 text-white px-8 text-center">
        <div className="flex flex-col items-center gap-4">
          <RotateCcw className="w-12 h-12 text-neutral-500 animate-pulse" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">DJ mode</div>
          <div className="text-xl font-semibold">Rotate your device</div>
          <div className="text-sm text-neutral-400 max-w-xs">
            Turn your phone sideways to mix two decks at once.
          </div>
        </div>
      </div>
    );
  }

  const activeBpmForStrategy = (() => {
    if (DjStore.deckA.isPlaying && DjStore.deckA.analysis) return DjStore.deckA.analysis.bpm;
    if (DjStore.deckB.isPlaying && DjStore.deckB.analysis) return DjStore.deckB.analysis.bpm;
    return null;
  })();
  const nextFeatures = DjStore.nextTrack
    ? DjStore.featureCache[DjStore.nextTrack.mediaId]
    : null;
  const nextBpm = nextFeatures?.bpm ?? null;
  const nextCamelot = nextFeatures?.camelot ?? null;
  const nextEnergy = nextFeatures?.energy ?? null;
  const autoStrategy =
    activeBpmForStrategy && nextBpm
      ? chooseStrategy(activeBpmForStrategy, nextBpm)
      : null;

  return (
    <div className="flex flex-col h-full w-full bg-neutral-900 text-white overflow-y-auto thin-scroll lg:overflow-hidden">
      <div className="flex-1 flex flex-col landscape:flex-row gap-2 landscape:gap-3 p-2 landscape:p-3 min-h-0">
        <Deck
          side="A"
          deck={DjStore.deckA}
          audioRef={audioARef}
          onPlayPause={() => handlePlayPause("A")}
          onCue={() => handleCue("A")}
          onSync={() => handleSync("A")}
          onLoadedMetadata={() => connectDeck("A")}
          onTimeUpdate={(ms) => DjStore.setDeckTime("A", ms)}
          onIsPlayingChange={(p) => DjStore.setDeckPlaying("A", p)}
          onUserRate={(r) => DjStore.setUserRate("A", r)}
        />
        <Deck
          side="B"
          deck={DjStore.deckB}
          audioRef={audioBRef}
          onPlayPause={() => handlePlayPause("B")}
          onCue={() => handleCue("B")}
          onSync={() => handleSync("B")}
          onLoadedMetadata={() => connectDeck("B")}
          onTimeUpdate={(ms) => DjStore.setDeckTime("B", ms)}
          onIsPlayingChange={(p) => DjStore.setDeckPlaying("B", p)}
          onUserRate={(r) => DjStore.setUserRate("B", r)}
        />
      </div>

      <NextUpPanel
        nextTrack={DjStore.nextTrack}
        nextBpm={nextBpm}
        nextCamelot={nextCamelot}
        nextEnergy={nextEnergy}
        breakdown={DjStore.lastPickBreakdown}
        autoStrategy={autoStrategy}
        override={DjStore.strategyOverride}
        setOverride={(v) => DjStore.setStrategyOverride(v)}
        queueMode={DjStore.queueMode}
        queueLength={DjStore.queue.length}
        playedCount={DjStore.playedIds.length}
        centerSlot={
          <MasterControls
            crossfade={DjStore.crossfade}
            setCrossfade={(v) => DjStore.setCrossfade(v)}
            autoTransition={DjStore.autoTransition}
            transitionFading={DjStore.transitionFading}
            onAutoTransition={handleAutoTransition}
            onRevertTransition={revertTransition}
            onAccelerateTransition={accelerateTransition}
            queueMode={DjStore.queueMode}
            onToggleQueueMode={startQueueMode}
            queueLength={DjStore.queue.length}
            transitionAvailable={!!(DjStore.deckA.track || DjStore.deckB.track) || DjStore.queueMode}
          />
        }
      />
    </div>
  );
}

export default inject("DjStore")(observer(Dj));
